import { Button, Divider, List, ListSubheader, FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { createStyles, makeStyles } from '@material-ui/styles';
import { MobilePageDefinition } from '../services';
import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { FormContext } from '..';
import { AppState } from '../../../../redux';
import { getFullFormVersion } from '../../NewEvalButton';
import { TableOfContentsPage } from './TableOfContentsPage';
import { CodeOutlined } from '@material-ui/icons';
import { JsonPreview } from '../../JsonPreview';
import { CURRENT_USER } from '../../../../utils/types';
import { UserDescription } from '../../../../redux/users';
import { RufasInputs } from './RufasInputs';

interface TableOfContentsProps {
  pages: readonly {definition: MobilePageDefinition, index: number, isVisible: boolean}[];
  currentPage: number;
  currentCard: number;
  onPageSelected: (page: number) => void;
  onCardSelected: (card: number) => void;
}

const useStyles = makeStyles(function (theme: Theme) {
  return createStyles({
    toc: {
      flex: 1,
      padding: theme.spacing(0, 2),
      overflowY: "scroll",
      WebkitOverflowScrolling: "touch",
    },
    tocHeader: {
      width: "100%",
      position: "sticky",
      backgroundColor: theme.palette.background.paper,
      padding: 0,
      flex: 1,
      display: 'flex',
      paddingTop: '12px',
    },
    label: {
      fontSize: '0.8rem',
      marginLeft: 0
    },
    labelWrapper: {
      paddingLeft: '1rem'
    },
    tocHeaderContent: {
      flex: 1
    },
    tocHeaderTitleCommon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    tocHeaderTitleDeskAudit: {
      paddingLeft: '1rem',
      fontSize: '0.8rem',
      lineHeight: 'normal',
      marginBottom: '0px'
    },
    tocHeaderTitle: {
      paddingLeft: '1rem',
      fontSize: '0.8rem',
      lineHeight: 'normal',
    },
    deskAuditButton: {
      textTransform: "none",
      minWidth: '108px'
    },
    buttons: {
      display: 'flex',
      gap: '8px',
    },
    showCode: {
      margin: 0,
      padding: 0,
      minWidth: '24px'
    },
    showRufas: {
      margin: 0,
      padding: 0,
      minWidth: '24px'
    }
  });
});

const selectActiveDefinition = createSelector(
  (state: AppState) => state.forms.activeFormDefinition, definition => definition
)

const selectCurrentUserFetched = createSelector(
  (state: AppState) => state.session.currentUserFetched, ready => ready
)

// better to use state and a reducer?
// with state and reducer, can access inside the appoverlay
export const TableOfContents: FC<TableOfContentsProps> = function (props) {
  const { pages, currentPage, onPageSelected, onCardSelected } = props;
  const classes = useStyles({});
  const { form, toggleDeskAudit, isDeskAudit } = useContext(FormContext);
  const formContext = useContext(FormContext);
  const [showJSONPreview, setShowJSONPreview] = useState(false);
  const [showRufasInputs, setShowRufasInputs] = useState(false);
  const currentUserFetched = useSelector(selectCurrentUserFetched);
  const [user, setUser] = useState<UserDescription | null>(null);

  const activeDefinition = useSelector(selectActiveDefinition, shallowEqual);

  const isDevelopment = process.env.NODE_ENV! === 'development';

  useEffect(function correctInvalidPge() {
    // If the default page is no longer valid, go to the first valid page
    if (!pages[currentPage].isVisible) {
      props.onPageSelected(pages.find(def => def.isVisible)?.index || 0);
    }
  }, [pages, currentPage]);

  useEffect(() => {
    if (!currentUserFetched) {
      setUser(null);
      return;
    }

    setUser(
      JSON.parse(localStorage.getItem(CURRENT_USER) as string)
    )
  }, [currentUserFetched])

  const toggleJSONPreview = useCallback(() => {
    setShowJSONPreview(!showJSONPreview);
  }, [showJSONPreview]);

  const toggleRufasInputs = useCallback(() => {
    setShowRufasInputs(!showRufasInputs);
  }, [showRufasInputs]);
  
  return <List
      component = { "nav" }
      className = { classes.toc }
      subheader = {
        <ListSubheader component = { "div" } className = { classes.tocHeader }>
          <div className = { classes.tocHeaderContent }>
            <div className = { [classes.tocHeaderTitleCommon, (activeDefinition && activeDefinition.desk_audit ? classes.tocHeaderTitleDeskAudit : classes.tocHeaderTitle)].join(' ') }>
              <span>{ form.short_title } { getFullFormVersion(form) }</span>
              <div className={ classes.buttons }>
                { (isDevelopment || user?.roles.includes('ROLE_ADMIN') || user?.organization === 'NDFP') &&
                  <Button
                    className = { classes.showCode }
                    onClick={ toggleJSONPreview }
                    title="View JSON">
                    <CodeOutlined></CodeOutlined>
                  </Button>
                }          
                { form.form_id.startsWith('farm_es_v3') && (isDevelopment || user?.roles.includes('ROLE_ADMIN') || user?.organization === 'NDFP') &&
                  <Button
                    className = { classes.showRufas }
                    onClick={ toggleRufasInputs }
                    title="View Rufas Inputs">
                    <img width="16" height="16" src="https://avatars.githubusercontent.com/u/41019370?s=16&v=4" alt="Rufas Inputs"/>
                  </Button>
                }
              </div>
            </div>
            {activeDefinition && activeDefinition.desk_audit && (
              <FormControlLabel
                checked={ isDeskAudit }
                classes={{
                    root: classes.labelWrapper,
                    label: classes.label,
                }}
                control={<Checkbox color="primary" onClick={ toggleDeskAudit }/>}
                label="Desk Evaluation"
                labelPlacement="end"
              />
            )}
          </div>
          <Divider variant={"fullWidth"}></Divider>
        </ListSubheader>
      }>
          { pages.map((desc, index) => desc.isVisible
              ? <TableOfContentsPage
                  key = { index }
                  desc = { desc }
                  index = { index }
                  onPageSelected = { onPageSelected }
                  onCardSelected = { onCardSelected }
                  currentPage = { currentPage === index }>
                </TableOfContentsPage>
              : null
          )}

          <Dialog
            open = { showJSONPreview }
            onClose = { toggleJSONPreview }
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
            maxWidth="xl"
          >
            <FormContext.Provider
              value = { Object.assign(
                Object.assign({}, formContext)
              )}>
              <DialogTitle id="alert-dialog-title">
                JSON Preview
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description" component="div">
                  <JsonPreview></JsonPreview>                  
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={ toggleJSONPreview }>
                  Close
                </Button>
              </DialogActions>
            </FormContext.Provider>
          </Dialog>

          <Dialog
            open = { showRufasInputs }
            onClose = { toggleRufasInputs }
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
            maxWidth="xl"
          >
            <FormContext.Provider
              value = { Object.assign(
                Object.assign({}, formContext)
              )}>
              <DialogTitle id="alert-dialog-title">
                Rufas Inputs
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description" component="div">
                  <h5>Metadata</h5>
                  <RufasInputs module="metadata"></RufasInputs>
                  <h5>Data</h5>
                  <RufasInputs module="data"></RufasInputs>
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={ toggleRufasInputs }>
                  Close
                </Button>
              </DialogActions>
            </FormContext.Provider>
          </Dialog>
        </List>
  ;
};